import type { EventSummary, RunSummary } from "./types";

export interface StreamHandlers {
  onEvent(e: EventSummary): void;
  onRun(r: RunSummary): void;
  onError?(): void;
  /** Fires when the connection (re)establishes — use it to clear a
   *  "reconnecting…" hint even if no new event has arrived yet. */
  onOpen?(): void;
}

/**
 * Opens the dashboard live stream (SSE) and wires named events to handlers.
 * The browser's native EventSource retries automatically on drop — `onError`
 * is just a UI hint to show a "reconnecting…" state, not a fatal signal.
 *
 * Returns a cleanup function that closes the connection.
 */
export function openStream(handlers: StreamHandlers, envSlug?: string | null): () => void {
  // EventSource can't send the X-Deleggate-Env header, so scope via query param.
  const url = envSlug
    ? `/api/dashboard/stream?env=${encodeURIComponent(envSlug)}`
    : "/api/dashboard/stream";
  const es = new EventSource(url, { withCredentials: true });

  es.addEventListener("open", () => {
    handlers.onOpen?.();
  });
  es.addEventListener("event", (m) => {
    handlers.onEvent(JSON.parse((m as MessageEvent).data));
  });
  es.addEventListener("run", (m) => {
    handlers.onRun(JSON.parse((m as MessageEvent).data));
  });
  // "ping" is a keep-alive frame with no payload of interest — ignored.
  es.addEventListener("error", () => {
    handlers.onError?.();
  });

  return () => es.close();
}
